import { defineAbility } from '@casl/ability'
import { MerchantPermissions } from '@/rbac'
import { USER_ROLES } from '@/components/layouts/templates/AppTemplateMixin'

export function defineAbilityForUser (user) {
  return defineAbility((can) => {
    // Principle of Least Privilege: Grant permissions explicitly.

    // Items generally available to all users unless restricted by specific conditions below
    can('view', 'Home')
    can('view', 'Orders')
    can('view', 'All Orders')
    can('view', 'Work Orders')
    can('view', 'Rules')
    can('view', 'Customers')
    can('view', 'Products')
    can('view', 'Inventory')
    can('view', 'Inventory Status')
    can('view', 'Inventory History')
    can('view', 'Inventory Transfers')
    can('view', 'Receiving')
    can('view', 'Receiving Orders')
    can('view', 'On Hold Receiving')
    can('view', 'Returns')
    can('view', 'Analytics')
    can('view', 'Integrations')
    can('view', 'App Store')
    can('view', 'Settings')
    can('view', 'My Profile')
    can('view', 'Shipment Settings')
    can('view', 'Logout')


    const canViewUserManagement = [
      USER_ROLES.Client,
      USER_ROLES.Admin,
      USER_ROLES.ClientAdmin,
      USER_ROLES.DemoUser
    ].includes(user.role) &&
      user.isMerchantOrg &&
      user.loggedInOrganization &&
      MerchantPermissions.can('manage', 'identity_read')
    if (canViewUserManagement) {
      can('view', 'User Management')
    }

    // Permissions for non-white label users
    if (!user.isWhiteLabelUser) {
      can('view', 'Fulfillment Centers')
      can('view', 'Pricing')
      can('view', 'Manage Badges')
      can('view', 'API Tokens')
      can('view', 'API Settings')

      if (user.featureFlags?.EnableIdealDistributionNavigation) {
        can('view', 'Inventory Distribution')
      }
      if (user.featureFlags?.AppManagement) {
        can('view', 'OAuth Apps')
      }
      if (user.featureFlags?.WebhookManagement) {
        can('view', 'Webhooks')
      }

      // Further restricted by legacy role and other flags for non-white label users
      if (user.role !== USER_ROLES.ClientCompanyBillingRestriction) {
        can('view', 'Billing')
        can('view', 'Payment Methods')

        if (user.isUSCustomer && user.featureFlags?.['Payments.EnableShipBobCapital']) {
          can('view', 'ShipBob Capital')
        }
        if (user.featureFlags?.EnableNewPaymentUI) {
          can('view', 'Finances')
        }
      }
    }

    // Permissions based on legacy user role
    if (![
      USER_ROLES.ClientCompanyBillingRestriction,
      USER_ROLES.ClientCompanyOrderAccess,
      USER_ROLES.ClientCompanyInventoryOrderAccess
    ].includes(user.role)) {
      can('view', 'Customize Packing Slip')
      can('view', 'Company Profile')
      can('view', 'Email Notifications')
      can('view', 'Ship Option Mapping')
      can('view', 'My Apps')
      can('view', 'Store Management')
    }

    // Activity Log (more specific legacy role exclusion)
    if (![
      USER_ROLES.ClientCompanyBillingRestriction,
      USER_ROLES.ClientCompanyOrderAccess,
      USER_ROLES.ClientCompanyInventoryOrderAccess,
      USER_ROLES.Client,
      USER_ROLES.ClientAdmin,
      USER_ROLES.DemoUser
    ].includes(user.role)) {
      can('view', 'Activity Log')
    }

    // Gift Notes vs Custom Document Settings
    if (user.featureFlags?.['FulfillmentSettings.EnableCustomDocumentSettings']) {
      can('view', 'Custom Document Settings')
    } else {
      can('view', 'Gift Notes')
    }

    // Subaccount related permissions
    if (user.subAccountOrgs?.length > 1) {
      can('view', 'Switch Subaccounts')
    }

    const canManageSubAccounts = [
      USER_ROLES.Client,
      USER_ROLES.Admin,
      USER_ROLES.ClientAdmin
    ].includes(user.role) &&
      user.loggedInOrganization &&
      !user.loggedInOrganization.parent_organization_id
    if (canManageSubAccounts) {
      can('view', 'Manage Subaccounts')
    }
  })
}
